import { HttpClient } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private tokenSubject = new BehaviorSubject<string | null>(null); // Store token globally
  token$ = this.tokenSubject.asObservable(); // Observable to track token changes

  constructor(
    private http: HttpClient,
    @Inject(DOCUMENT) private document: Document
  ) {
    // Preserve the token from URL before cleaning up query parameters
    if (typeof window !== 'undefined' && window.location && window.history && this.document) {
      // Extract token from URL BEFORE clearing query params
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      if (tokenFromUrl) {
        localStorage.setItem('jsonWebToken', tokenFromUrl);
      }
      
      // Clean up any query parameters from the URL to reduce referrer leakage
      if (window.location.search) {
        window.history.replaceState({}, this.document.title, window.location.pathname);
      }
    }

    // Fallback to stored token (session-scoped)
    const storage = this.getSessionStorage();
    if (storage) {
      const savedToken = storage.getItem('authToken');
      if (savedToken && this.isValidToken(savedToken)) {
        this.tokenSubject.next(savedToken);
      } else {
        // Clear any invalid or corrupted tokens from storage
        storage.removeItem('authToken');
      }
    }
  }

  // Centralized, guarded access to sessionStorage
  private getSessionStorage(): Storage | null {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      if (!('sessionStorage' in window)) {
        return null;
      }
      return window.sessionStorage;
    } catch {
      return null;
    }
  }

  // Fetch Token from API (can be removed after deployed to cloudfront or cdn)
  fetchTokenFromAPI(email: string) {
    const encodedEmail = encodeURIComponent(email);
    this.http.get<{ token: string }>(`/api/Token/${encodedEmail}`).pipe(
      tap((response) => {
        this.setToken(response.token);
      }),
      switchMap(() => this.http.get('/Shared/Common/GetUserloginDetails')),
      tap((userDetails: any) => {
        localStorage.setItem('loggedInUserDetails', JSON.stringify(userDetails));
      })
    ).subscribe({
      next: () => {
        console.log('Token and user details fetched successfully');
      },
      error: (err) => {
        console.error('Error fetching token or user details:', err);
      }
    });
  }

  // Set Token Globally
  setToken(token: string) {
    if (!this.isValidToken(token)) {
      console.warn('Attempted to set invalid auth token; ignoring.');
      return;
    }
    this.tokenSubject.next(token);
    const storage = this.getSessionStorage();
    if (storage) {
      storage.setItem('authToken', token);
    }
  }

  // Basic token format validation to avoid persisting arbitrary data
  private isValidToken(token: string): boolean {
    // Simple JWT-like pattern: three base64url-ish segments separated by dots
    const jwtPattern = /^[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+$/;
    return typeof token === 'string' && token.length > 0 && jwtPattern.test(token);
  }

  // Get Current Token
  getToken(): string | null {
    return this.tokenSubject.value;
  }

  // Clear Token on Logout
  clearToken() {
    this.tokenSubject.next(null);
    const storage = this.getSessionStorage();
    if (storage) {
      storage.removeItem('authToken');
    }
  }

}
